import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { StatTiles } from "../components/StatTiles";
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Spinner,
  StatusBadge,
} from "../components/primitives";
import { PROJECT_STATUS_TONE, ScoreControl } from "../components/ScoreControl";
import { api } from "../lib/api";
import { projectTypeLabel } from "../lib/labels";
import { count } from "../lib/format";

import type { Project, ProjectStatus } from "../lib/types";

export function Projects() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  const options = useQuery({ queryKey: ["options"], queryFn: api.options });
  const playbooks = useQuery({ queryKey: ["playbooks"], queryFn: api.playbooks });
  // A run finishing flips a project from ongoing to completed or failed, and
  // nothing on this page is subscribed to that run's event stream. Polling is
  // the honest fix: two small queries, and the row stops lying about a project
  // that finished while you were looking at it.
  const summary = useQuery({
    queryKey: ["project-summary"],
    queryFn: api.projectSummary,
    refetchInterval: 15_000,
  });
  const projects = useQuery({
    queryKey: ["projects", status, search],
    queryFn: () =>
      api.projects({
        status: status === "all" ? undefined : status,
        search: search || undefined,
      }),
    refetchInterval: 15_000,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    queryClient.invalidateQueries({ queryKey: ["project-summary"] });
  };

  if (projects.isLoading) return <Spinner label="Loading projects" />;
  if (projects.isError) return <ErrorNote error={projects.error} />;

  const rows = projects.data ?? [];
  const stats = summary.data;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Projects</h1>
        <Button variant="primary" onClick={() => setCreating(true)}>
          New project
        </Button>
      </div>

      {/* The same boxes the Engagements list opens with: two lists of the
          firm's work should not be read two different ways. */}
      {stats && (
        <StatTiles
          items={[
            {
              value: count(stats.total),
              label: stats.total === 1 ? "project" : "projects",
              onClick: () => setStatus("all"),
              active: status === "all",
            },
            {
              value: count(stats.by_status.planned ?? 0),
              label: "planned",
              onClick: () => setStatus("planned"),
              active: status === "planned",
            },
            {
              value: count(stats.by_status.ongoing ?? 0),
              label: "ongoing",
              tone: (stats.by_status.ongoing ?? 0) > 0 ? "warning" : undefined,
              onClick: () => setStatus("ongoing"),
              active: status === "ongoing",
            },
            {
              value: count(stats.by_status.completed ?? 0),
              label: "completed",
              onClick: () => setStatus("completed"),
              active: status === "completed",
            },
            {
              value: count(stats.by_status.failed ?? 0),
              label: "failed",
              tone: (stats.by_status.failed ?? 0) > 0 ? "critical" : undefined,
              onClick: () => setStatus("failed"),
              active: status === "failed",
            },
            {
              value: count(
                rows.filter((p) => p.status === "completed" && p.score === null).length,
              ),
              label: "awaiting a score",
            },
            {
              value: stats.average_score !== null ? String(stats.average_score) : "—",
              label: "mean score",
              hint: "1 is excellent, 6 is bad",
            },
          ]}
        />
      )}

      {(creating || editing) && (
        <ProjectForm
          project={editing}
          projectTypes={options.data?.project_types ?? []}
          statuses={options.data?.project_statuses ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            refresh();
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
          <select
            className="field w-auto py-1.5"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {(options.data?.project_statuses ?? []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            className="field w-auto flex-1 py-1.5"
            placeholder="Search name, client or target"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="font-mono text-sm tabular-nums text-muted">{count(rows.length)}</span>
        </div>

        {rows.length === 0 ? (
          <Empty
            title="No projects"
            hint="Projects are created automatically when an engagement runs, or you can register one up front as planned."
          />
        ) : (
          <table className="w-full text-base">
            <thead>
              <tr className="label border-b border-line text-left">
                <th className="px-4 py-2.5 font-medium">Project</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Dates</th>
                <th className="px-4 py-2.5 font-medium">Score</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((project) => (
                <tr key={project.id} className="hover:bg-surface-2">
                  <td className="px-4 py-3">
                    <span className="font-medium">{project.name}</span>
                    <span className="mt-0.5 block text-sm text-muted">
                      {project.client ?? "internal"}
                      {project.run_id && (
                        <>
                          {" · "}
                          <Link
                            to={`/engagements/${encodeURIComponent(project.run_id)}`}
                            className="text-accent underline underline-offset-2"
                          >
                            run
                          </Link>
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-2">
                    {projectTypeLabel(playbooks.data, project.project_type)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge tone={PROJECT_STATUS_TONE[project.status]}>
                      {project.status}
                    </StatusBadge>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted">
                    {project.start_date ?? "—"}
                    {project.end_date ? ` → ${project.end_date}` : ""}
                  </td>
                  <td className="px-4 py-3">
                    <ScoreControl project={project} onSaved={refresh} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {(project.status === "planned" ||
                        project.status === "failed") && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/new?project=${project.id}`)}
                        >
                          {project.status === "failed" ? "Retry" : "Run"}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(project)}
                      >
                        Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function ProjectForm({
  project,
  projectTypes,
  statuses,
  onClose,
  onSaved,
}: {
  project: Project | null;
  projectTypes: string[];
  statuses: ProjectStatus[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(project?.name ?? "");
  const [client, setClient] = useState(project?.client ?? "");
  const [type, setType] = useState(project?.project_type ?? "due diligence");
  const [status, setStatus] = useState<ProjectStatus>(project?.status ?? "planned");
  const [description, setDescription] = useState(project?.description ?? "");
  const [startDate, setStartDate] = useState(project?.start_date ?? "");
  const [endDate, setEndDate] = useState(project?.end_date ?? "");

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        client: client.trim() || null,
        project_type: type,
        status,
        description: description.trim() || null,
        start_date: startDate || null,
        end_date: endDate || null,
      } as Partial<Project>;
      return project
        ? api.updateProject(project.id, body)
        : api.createProject(body);
    },
    onSuccess: onSaved,
  });

  return (
    <Card title={project ? `Edit ${project.name}` : "New project"}>
      <form
        className="grid gap-4 p-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="p-name">
            Name *
          </label>
          <input
            id="p-name"
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div>
          <label className="field-label" htmlFor="p-client">
            Client
          </label>
          <input
            id="p-client"
            className="field"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            placeholder="internal"
          />
        </div>

        <div>
          <label className="field-label" htmlFor="p-type">
            Type
          </label>
          <select
            id="p-type"
            className="field"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {projectTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="field-label" htmlFor="p-status">
            Status
          </label>
          <select
            id="p-status"
            className="field"
            value={status}
            onChange={(e) => setStatus(e.target.value as ProjectStatus)}
          >
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {project?.score !== null && project?.score !== undefined && (
            <p className="mt-1.5 text-xs text-muted">
              This project is scored — clear the score before moving it out of
              completed.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label" htmlFor="p-start">
              Start
            </label>
            <input
              id="p-start"
              type="date"
              className="field"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="p-end">
              End
            </label>
            <input
              id="p-end"
              type="date"
              className="field"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="p-description">
            Description
          </label>
          <textarea
            id="p-description"
            className="field min-h-20 resize-y"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {save.isError && (
          <div className="sm:col-span-2">
            <ErrorNote error={save.error} />
          </div>
        )}

        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={!name.trim() || save.isPending}>
            {save.isPending ? "Saving…" : project ? "Save changes" : "Create project"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
